import { useEffect, useState } from "react";
import { Card, CardBody, Col, FormGroup, Input, Label, Row, TabPane } from "reactstrap";
import { post } from "../../../../../utils/helpers/api";
import { toast } from "react-toastify";
import DefaultButton from "../../../../../components/Buttons/Default";
import { useTranslation } from "react-i18next";

function AuthenticationTab({ settings }) {
  const { t } = useTranslation();
  const [saveLoading, setSaveLoading] = useState(false);
  const [twoFA, setTwoFA]             = useState(true);
  const [minLength, setMinLength]     = useState(8);
  const [passwordExpiry, setPasswordExpiry] = useState(90);
  const [reqUpper, setReqUpper]       = useState(true);
  const [reqNumber, setReqNumber]     = useState(true);
  const [reqSpecial, setReqSpecial]   = useState(false);
  const [sessionTimeout, setTimeout_] = useState('60');
  const [rememberDevice, setRemember] = useState(true);

  useEffect(() => {
    setTwoFA(Number(settings?.security?.['auth.enabled_2fa']) === 1 ? true : false);
    setMinLength(settings?.security?.['auth.password_min_length'] || 8);
    setPasswordExpiry(settings?.security?.['auth.password_expiry_days'] || 90);
    setReqUpper(Number(settings?.security?.['auth.password_required_uppercase']) === 1 ? true : false);
    setReqNumber(Number(settings?.security?.['auth.password_required_number']) === 1 ? true : false);
    setReqSpecial(Number(settings?.security?.['auth.password_required_special']) === 1 ? true : false);
    setTimeout_(settings?.security?.['auth.session_timeout_minutes'] || '60');
    setRemember(Number(settings?.security?.['auth.remember_devices']) === 1 ? true : false);
  }, [settings]);

  const saveSettings = () => {
    setSaveLoading(true);

    const data = post('/settings/security/authentication', {
      enabled_2fa: twoFA,
      password_min_length: minLength,
      password_expiry_days: passwordExpiry,
      password_required_uppercase: reqUpper,
      password_required_number: reqNumber,
      password_required_special: reqSpecial,
      session_timeout_minutes: sessionTimeout,
      remember_devices: rememberDevice,
    });

    if(data?.success) {
      toast.success(t('security.settingsSaved'));
    }

    setSaveLoading(false);
  }

  return(
    <TabPane tabId="authentication">
      {/* 2FA */}
      <Card className="mb-3">
        <CardBody>
          <p className="text-muted small fw-semibold text-uppercase mb-3">{t('security.twoFactor')}</p>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <p className="fw-medium mb-0">{t('security.enable2FA')}</p>
              <small className="text-muted">{t('security.twoFactorDesc')}</small>
            </div>
            <div className="form-check form-switch mb-0">
              <input className="form-check-input" type="checkbox" role="switch" checked={twoFA} onChange={() => setTwoFA(!twoFA)} />
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Password policy */}
      <Card className="mb-3">
        <CardBody>
          <p className="text-muted small fw-semibold text-uppercase mb-3">{t('security.passwordPolicy')}</p>
          <Row>
            <Col lg="4">
              <FormGroup>
                <Label>{t('security.minimumLength')}</Label>
                <Input
                  type="number"
                  min="6"
                  max="32"
                  value={minLength}
                  onChange={e => setMinLength(e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col lg="4">
              <FormGroup>
                <Label>{t('security.passwordExpiry')}</Label>
                <Input type="select" value={passwordExpiry} onChange={e => setPasswordExpiry(e.target.value)}>
                  <option value="">{t('security.never')}</option>
                  <option value="30">{t('security.days30')}</option>
                  <option value="60">{t('security.days60')}</option>
                  <option value="90">{t('security.days90')}</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
          {[
            { label: t('security.requireUppercase'), val: reqUpper,   set: setReqUpper   },
            { label: t('security.requireNumbers'),   val: reqNumber,  set: setReqNumber  },
            { label: t('security.requireSpecial'),   val: reqSpecial, set: setReqSpecial },
          ].map((item, i) => (
            <div key={i} className="d-flex justify-content-between align-items-center py-2 border-bottom">
              <span>{item.label}</span>
              <div className="form-check form-switch mb-0">
                <input className="form-check-input" type="checkbox" role="switch" checked={item.val} onChange={() => item.set(!item.val)} />
              </div>
            </div>
          ))}
        </CardBody>
      </Card>

      {/* Session settings */}
      <Card className="mb-3">
        <CardBody>
          <p className="text-muted small fw-semibold text-uppercase mb-3">{t('security.sessionSettings')}</p>
          <Row>
            <Col lg="4">
              <FormGroup>
                <Label>{t('security.sessionTimeout')}</Label>
                <Input type="select" value={sessionTimeout} onChange={e => setTimeout_(e.target.value)}>
                  <option value="15">{t('security.minutes15')}</option>
                  <option value="30">{t('security.minutes30')}</option>
                  <option value="60">{t('security.hours1')}</option>
                  <option value="240">{t('security.hours4')}</option>
                  <option value="480">{t('security.hours8')}</option>
                  <option value="0">{t('security.never')}</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <div className="d-flex justify-content-between align-items-center pt-2">
            <div>
              <p className="mb-0 fw-medium">{t('security.rememberDevice')}</p>
              <small className="text-muted">{t('security.skip2FA')}</small>
            </div>
            <div className="form-check form-switch mb-0">
              <input className="form-check-input" type="checkbox" role="switch" checked={rememberDevice} onChange={() => setRemember(!rememberDevice)} />
            </div>
          </div>
        </CardBody>
      </Card>
      <DefaultButton
        color="primary"
        size="sm"
        onClick={saveSettings}
        loading={saveLoading}
      >
        {t('common.save')}
      </DefaultButton>
    </TabPane>
  );
}

export default AuthenticationTab;
